import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { NamedLink } from '../../../components';
import { kycStateOf, setupNeedsAction } from '../../../util/memberArea';
import MemberIcon from '../MemberIcon';

import css from './AvatarMenu.module.css';

/**
 * The avatar menu (member area N1; Member Home design, decisions D16–D17):
 * name, plan (with Upgrade for free accounts), Profile, Deal notifications,
 * KYC verification, Settings (the password page), About, Admin dashboard
 * (admins only) and Log out. Profile, Deal notifications, KYC and Settings are
 * the account pages (AccountLayout).
 *
 * A dot on the avatar and on KYC while setup needs the member to act.
 */

// Same statuses as the paywall (src/util/misc.js userHasValidSubscription).
const PAID_STATUSES = ['active', 'legacy', 'offplatform-subscription'];

// The membership tiers, named as on the membership page.
const PLAN_NAMES = {
  'Monthly Subscription': 'Core Deal Access',
  'Quarterly Subscription': 'Core Deal Access',
  'Annual Subscription': 'Core Deal Access',
  'Multi Desk Quarterly': 'Multi-Desk',
  'Multi Desk Yearly': 'Multi-Desk',
  'Priority Managed': 'Priority Managed',
  Partner: 'Partner',
};

/** "Free account", or the member's plan. Pure. */
export const planOf = user => {
  const metadata = user?.attributes?.profile?.metadata || {};
  if (!PAID_STATUSES.includes(metadata.subscriptionStatus)) {
    return { paid: false, label: 'Free account' };
  }
  return { paid: true, label: PLAN_NAMES[metadata.subscriptionName] || 'Member' };
};

const KYC_SUB = { none: 'Not submitted', submitted: 'Under review', approved: 'Verified' };

/** The initials shown in the round avatar. Pure. */
export const initialsOf = user => {
  const profile = user?.attributes?.profile || {};
  if (profile.abbreviatedName) return profile.abbreviatedName;
  return `${(profile.firstName || '').charAt(0)}${(profile.lastName || '').charAt(0)}`.toUpperCase();
};

const Item = ({ icon, label, sub, dot, ...linkProps }) => (
  <NamedLink className={css.item} {...linkProps}>
    <span className={css.itemIcon}>
      <MemberIcon name={icon} size={16} />
    </span>
    <span className={css.itemText}>
      <span className={css.itemLabel}>{label}</span>
      {sub ? <span className={css.itemSub}>{sub}</span> : null}
    </span>
    {dot ? <span className={css.dot} /> : null}
  </NamedLink>
);

const AvatarMenu = props => {
  const { currentUser, onLogout, className } = props;
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = e => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const profile = currentUser?.attributes?.profile || {};
  const name = `${profile.firstName || ''} ${profile.lastName || ''}`.trim() || profile.displayName;
  const plan = planOf(currentUser);
  const kyc = kycStateOf(currentUser);
  const needsAction = setupNeedsAction(currentUser);
  const isAdmin = !!profile.metadata?.isAdmin;

  return (
    <div className={classNames(css.root, className)} ref={rootRef}>
      <button
        type="button"
        className={css.avatar}
        onClick={() => setOpen(!open)}
        aria-label="Your account"
        aria-expanded={open}
      >
        {initialsOf(currentUser)}
        {needsAction ? <span className={css.avatarDot} /> : null}
      </button>

      {open ? (
        <div className={css.panel} onClick={() => setOpen(false)}>
          <div className={css.head}>
            <span className={css.name}>{name}</span>
            <div className={css.planRow}>
              <span className={css.plan}>{plan.label}</span>
              {!plan.paid ? (
                <NamedLink name="SubscriptionsPage" className={css.upgrade}>
                  Upgrade
                </NamedLink>
              ) : null}
            </div>
          </div>
          <Item icon="profile" label="Profile" name="ProfileSettingsPage" />
          {/* Members Area design: "Deal notifications" (notification settings
              and deal preferences), its own page (user, 2026-10-27). */}
          <Item icon="sliders" label="Deal notifications" name="DealNotificationsPage" />
          <Item
            icon={kyc === 'approved' ? 'kycOk' : 'kyc'}
            label="KYC verification"
            sub={KYC_SUB[kyc]}
            dot={kyc === 'none'}
            name="UploadPassportPage"
          />
          {/* Settings = the password page (user, 2026-10-27). */}
          <Item icon="settings" label="Settings" name="PasswordChangePage" />
          <Item icon="about" label="About" name="AboutPage" />
          {isAdmin ? <Item icon="dashboard" label="Admin dashboard" name="AdminDashboardPage" /> : null}
          <button type="button" className={css.item} onClick={onLogout}>
            <span className={css.itemIcon}>
              <MemberIcon name="logout" size={16} />
            </span>
            <span className={css.itemText}>
              <span className={css.itemLabel}>Log out</span>
            </span>
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default AvatarMenu;
